import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Loader2, X } from 'lucide-react';
import { OrgReportsApi } from '@/services/org-reports.api';
import {
  TABS,
  TabKey,
  PortfolioHeader,
  PersonalTab,
  GuardianTab,
  AcademicTab,
  AttendanceTab,
  FeesTab,
} from '@/pages/reports/StudentPortfolioPage';
import { PageHeader } from '@/components/ui/PageHeader';
import { Input } from '@/components/ui/Input';

/**
 * Student Portfolio — org tier, read-only. Same search-then-detail flow
 * as the school-tier StudentPortfolioPage (reuses its tab components
 * directly, zero duplication), except the search step is cross-school
 * (OrgReportsApi.students(), already built for the Students Report list)
 * instead of one school's own roster, and each result carries its own
 * schoolId so the detail fetch knows which tenant schema to query.
 */
export function OrgStudentPortfolioPage() {
  const [searchInput, setSearchInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<{ schoolId: string; studentId: string } | null>(null);
  const [tab, setTab] = useState<TabKey>('personal');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: searchResult, isFetching: searching } = useQuery({
    queryKey: ['org-portfolio-search', debounced],
    queryFn: () => OrgReportsApi.students({ search: debounced, limit: 10 }),
    enabled: debounced.length >= 2 && !selected,
  });
  const suggestions = searchResult?.items ?? [];

  const { data: portfolio, isFetching: loadingPortfolio } = useQuery({
    queryKey: ['org-portfolio', selected],
    queryFn: () => OrgReportsApi.studentPortfolio(selected!.schoolId, selected!.studentId),
    enabled: !!selected,
  });

  const clearSelection = () => {
    setSelected(null);
    setSearchInput('');
    setDebounced('');
  };

  return (
    <>
      <PageHeader
        title="Student Portfolio"
        description="Search for a student across your organization's schools to see a consolidated view — personal, guardian, attendance, and fee details."
      />

      {!selected && (
        <div className="relative max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or admission #"
            className="pl-9"
          />
          {debounced.length >= 2 && (
            <div className="mt-1 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              {searching ? (
                <div className="p-4 text-center text-slate-400">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                </div>
              ) : suggestions.length === 0 ? (
                <div className="p-4 text-center text-sm text-slate-400">No students found.</div>
              ) : (
                suggestions.map((s) => (
                  <button
                    key={`${s.schoolId}-${s.studentId}`}
                    className="flex w-full items-center justify-between px-4 py-2 text-left text-sm hover:bg-slate-50"
                    onClick={() => {
                      setSelected({ schoolId: s.schoolId, studentId: s.studentId });
                      setTab('personal');
                    }}
                  >
                    <span className="font-medium text-slate-900">{s.studentName}</span>
                    <span className="text-slate-400">
                      {s.admissionNo} · {s.schoolName} · {s.className} {s.sectionName}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {selected && (
        <div className="max-w-4xl">
          <button className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" onClick={clearSelection}>
            <X className="h-4 w-4" /> Search another student
          </button>

          {loadingPortfolio || !portfolio ? (
            <div className="card p-8 text-center text-slate-400">
              <Loader2 className="mx-auto h-5 w-5 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              <PortfolioHeader student={portfolio.student} />

              <div className="flex flex-wrap items-center gap-1 border-b border-slate-200">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    className={`inline-flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium ${
                      tab === t.key ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <t.icon className="h-4 w-4" /> {t.label}
                  </button>
                ))}
              </div>

              {tab === 'personal' && <PersonalTab student={portfolio.student} />}
              {tab === 'guardian' && <GuardianTab guardian={portfolio.guardian} />}
              {tab === 'academic' && <AcademicTab />}
              {tab === 'attendance' && <AttendanceTab attendance={portfolio.attendance} />}
              {tab === 'fees' && <FeesTab fees={portfolio.fees} />}
            </div>
          )}
        </div>
      )}
    </>
  );
}
